<template>
  <ValidationObserver
    v-slot="{handleSubmit}"
    ref="formObserver"
  >
    <b-modal
      id="attributesModal"
      v-model="attributesModal.show"
      :title="`${form.attributeId ? 'Edit' : 'Create'} Attribute`"
      @hidden="hideModal"
      @ok.prevent="handleSubmit(onSubmit)"
      @hide="beforeHide"
      @show="onModalShow"
    >
      <content-spinner
        :show="attributesModal.loading"
        text="Please wait..."
      />
      <show-error
        :show="showError"
        :text="modalError"
      />
      <b-form
        v-if="!attributesModal.loading"
        @submit="handleSubmit(onSubmit)"
        @reset="handleSubmit(onReset)"
        @keydown.enter.prevent="handleSubmit(onSubmit)"
      >
        <input-widget
          ref="focusedField"
          :model="form"
          :field="fields.attributeDisplayName"
          :autofocus="true"
        />
        <input-widget
          :model="form"
          :field="fields.description"
        />
        <!--        <input-widget :model="form" :field="fields.tags"/>-->
        <input-widget
          :model="form"
          :field="fields.exampleValues"
        />
        <input-widget
          :model="form"
          :field="fields.reportingChangeType"
        />
        <input-widget
          :model="form"
          :field="fields.minorElement"
        />


        <b-button
          v-if="!expand"
          type="button"
          size="md"
          variant="primary"
          class="expand-button"
          @click="expand = true"
        >
          Show Advanced Options
        </b-button>

        <div v-if="expand">
          <input-widget
            :model="form"
            :field="fields.isOnPhysicalModel"
          />
          <input-widget
            :model="form"
            :field="fields.isOnLogicalModel"
          />
          <input-widget
            :model="form"
            :field="fields.dataType"
          />
          <input-widget
            id="characterLength"
            :model="form"
            :field="fields.characterLength"
          />
          <input-widget
            id="numericPrecision"
            :model="form"
            :field="fields.numericPrecision"
          />
          <input-widget
            id="numericScale"
            :model="form"
            :field="fields.numericScale"
          />
          <input-widget
            :model="form"
            :field="fields.defaultValue"
          />
          <!--          <input-widget :model="form" :field="fields.deploymentScript"/>-->
          <input-widget
            :model="form"
            :field="fields.aggregationType"
          />
        </div>
      </b-form>

      <template #modal-footer="{ok, cancel}">
        <modal-footer
          v-if="!form.attributeId"
          v-model="addAnother"
          :loading="attributesModal.loading"
          :cancel="cancel"
          :ok="ok"
          :disabled="showError"
        />
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>

import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../core/components/ContentSpinner';
import {extend, ValidationObserver, ValidationProvider} from 'vee-validate';
import {required} from 'vee-validate/dist/rules';
import InputWidget from '../../core/components/complex-table/components/InputWidget';
import ModalFooter from './components/ModalFooter';
import ShowError from '../../core/components/ShowError';

extend('required', {
  ...required,
  message: 'The {_field_} field is required.',
});

const {mapState, mapActions} = createNamespacedHelpers('sunModel');
export default {
  name: 'AttributesModal',
  components: {ShowError, ModalFooter, InputWidget, ContentSpinner, ValidationObserver, ValidationProvider},
  data() {
    return {
      expand: false,
      showError: false,
      modalError: '',
      form: {
        attributeDisplayName: '',
        description: '',
        // tags: [],
        exampleValues: '',
        reportingChangeType: 'type1',
        minorElement: false,
        isOnPhysicalModel: true,
        isOnLogicalModel: true,
        dataType: null,
        numericPrecision: 16,
        numericScale: 8,
        characterLength: 255,
        defaultValue: null,
        // deploymentScript: null,
        aggregationType: null,
      },
      addAnother: false,
      fields: {
        attributeDisplayName: {
          type: 'text',
          required: true,
          label: 'Name',
          name: 'attributeDisplayName',
        },
        description: {
          type: 'text',
          required: false,
          label: 'Description',
          name: 'description',
        },
        /*tags: {
          type: 'tags',
          required: false,
          label: 'Tags',
          name: 'tags'
        },*/
        exampleValues: {
          type: 'text',
          required: false,
          label: 'Example Values',
          name: 'exampleValues',
        },
        reportingChangeType: {
          type: 'radio-group',
          required: false,
          label: 'Change Type',
          name: 'reportingChangeType',
          data: {
            options: [
              {value: 'type1', text: 'Type 1'},
              {value: 'type2', text: 'Type 2'},
            ],
          },
        },
        minorElement: {
          type: 'checkbox',
          required: false,
          label: 'Minor Element',
          name: 'minorElement',
        },
        isOnPhysicalModel: {
          type: 'checkbox',
          required: false,
          label: 'Include On Physical Model',
          name: 'isOnPhysicalModel',
        },
        isOnLogicalModel: {
          type: 'checkbox',
          required: false,
          label: 'Include On Logical Model',
          name: 'isOnLogicalModel',
        },
        dataType: {
          type: 'select',
          required: false,
          label: 'Data Type',
          name: 'dataType',
          data: {
            options: [],
          },
        },
        characterLength: {
          type: 'number',
          required: false,
          label: 'Character Length',
          name: 'characterLength',
        },
        numericPrecision: {
          type: 'number',
          required: false,
          label: 'Numeric Precision',
          name: 'numericPrecision',
        },
        numericScale: {
          type: 'number',
          required: false,
          label: 'Numeric Scale',
          name: 'numericScale',
        },
        defaultValue: {
          type: 'number',
          required: false,
          label: 'Default Value',
          name: 'defaultValue',
        },
        /*deploymentScript: {
          type: 'text',
          required: false,
          label: 'Deployment Script',
          name: 'deploymentScript',
        },*/
        aggregationType: {
          type: 'select',
          required: false,
          label: 'Aggregation Type',
          name: 'aggregationType',
          data: {
            options: [],
          },
        },
      },
    };
  },
  watch: {
    ['form.characterLength']() {
      if (this.form.characterLength) {
        if (this.form.characterLength > 4000 || this.form.characterLength < -1) {
          this.showError = true;
          this.modalError = `${this.fields.characterLength.label} Input must be numeric between -1 and 4000`;
        } else {
          this.resetError();
        }
      }
    },
    ['form.numericPrecision']() {
      if (this.form.numericPrecision) {
        if (this.form.numericPrecision > 38 || this.form.numericPrecision < 1) {
          this.showError = true;
          this.modalError = `${this.fields.numericPrecision.label} Input must be numeric between 1 and 38`;
        } else if (this.form.numericScale <= 30 && this.form.numericScale >= 1) {
          this.resetError();
        } else {
          this.modalError = `${this.fields.numericScale.label} Input must be numeric between 1 and 30`;
        }
      }
    },
    ['form.numericScale']() {
      if (this.form.numericScale) {
        if (this.form.numericScale > 30 || this.form.numericScale < 1) {
          this.showError = true;
          this.modalError = `${this.fields.numericScale.label} Input must be numeric between 1 and 30`;
        } else if (this.form.numericPrecision <= 38 && this.form.numericPrecision >= 1) {
          this.resetError();
        } else {
          this.modalError = `${this.fields.numericPrecision.label} Input must be numeric between 1 and 38`;
        }
      }
    },
    ['form.dataType']() {
      if (this.form.dataType) {
        this.changeFieldsHidden();
      }
      this.resetError();
    },
    ['expand']() {
      if (this.expand) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.changeFieldsHidden();
          });
        });
      }
    },
  },
  computed: {
    ...mapState(['attributesModal']),
  },
  methods: {
    ...mapActions(['hideAttributesModal', 'addAttribute', 'updateAttribute']),
    onModalShow() {
      this.form = {...this.attributesModal.data};
      this.fields.aggregationType.data.options = this.attributesModal.aggregationTypes;
      this.fields.dataType.data.options = this.attributesModal.dataTypes;
      this.setDefaultOptions();
    },
    setDefaultOptions() {
      if (this.attributesModal.entityType === 'fact') {
        const index = this.attributesModal.aggregationTypes.findIndex(type => type === 'Sum');
        this.form.aggregationType = this.form.aggregationType ? this.form.aggregationType : this.attributesModal.aggregationTypes[index];
      } else {
        this.form.aggregationType = this.form.aggregationType ? this.form.aggregationType : this.attributesModal.aggregationTypes[0];
      }
      this.form.dataType = this.form.dataType ? this.form.dataType : this.attributesModal.dataTypes[0];
    },
    resetError() {
      this.showError = false;
      this.modalError = '';
    },
    beforeHide(e) {
      this.expand = false;
      if (this.attributesModal.loading) {
        e.preventDefault();
        return false;
      }
    },
    changeFieldsHidden() {
      const characterLength = document.getElementById('characterLength');
      const numericPrecision = document.getElementById('numericPrecision');
      const numericScale = document.getElementById('numericScale');

      if (characterLength && numericPrecision && numericScale) {
        if (this.form.dataType === 'Text') {
          this.form.characterLength = this.form.characterLength ? this.form.characterLength : 8;
          characterLength.hidden = false;
          numericPrecision.hidden = true;
          numericScale.hidden = true;
        } else if (this.form.dataType === 'Decimal') {
          this.form.numericPrecision = this.form.numericPrecision ? this.form.numericPrecision : 16;
          this.form.numericScale = this.form.numericScale ? this.form.numericScale : 8;
          characterLength.hidden = true;
          numericPrecision.hidden = false;
          numericScale.hidden = false;
        } else {
          characterLength.hidden = true;
          numericPrecision.hidden = true;
          numericScale.hidden = true;
        }
      }
    },
    async onSubmit(e) {
      // e.preventDefault();
      let params = {...this.form};
      params.entityId = this.attributesModal.entityId;

      let action = 'create';
      if (params.attributeId) {
        params = {
          attributeId: params.attributeId,
          entityId: params.entityId,
          attributeDisplayName: params.attributeDisplayName,
          description: params.description,
          exampleValues: params.exampleValues,
          reportingChangeType: params.reportingChangeType,
          minorElement: params.minorElement,
          isOnPhysicalModel: params.isOnPhysicalModel,
          isOnLogicalModel: params.isOnLogicalModel,
          dataType: params.dataType,
          aggregationType: params.aggregationType,
          numericPrecision: params.numericPrecision,
          numericScale: params.numericScale,
          characterLength: params.characterLength,
          defaultValue: params.defaultValue,
        };
        action = 'update';
      } else {
        action = 'create';
      }

      if (params.dataType === 'Text') {
        delete params.numericPrecision;
        delete params.numericScale;
      } else if (params.dataType === 'Decimal') {
        delete params.characterLength;
      } else {
        delete params.numericPrecision;
        delete params.numericScale;
        delete params.characterLength;
      }

      // delete params.tags
      if (!this.showError) {
        let res;
        if (action === 'create') {
          res = await this.addAttribute(params);
        } else {
          res = await this.updateAttribute(params);
        }
        if (res.success) {
          if (!this.addAnother) {
            this.hideModal();
          }
          this.$toasted.success('Attribute created successfully', {
            duration: TOASTER_DURATION,
          });
          this.resetModal();
          this.$refs.focusedField.$refs.currentInput.focus();
        } else {
          this.$toasted.error(res.body.ErrorMessage, {
            duration: TOASTER_DURATION,
          });
        }
      }
    },
    resetModal() {
      this.form = {
        attributeDisplayName: '',
        description: '',
        // tags: [],
        exampleValues: '',
        reportingChangeType: 'type1',
        minorElement: false,
        isOnPhysicalModel: true,
        isOnLogicalModel: true,
        dataType: null,
        numericPrecision: 16,
        numericScale: 8,
        characterLength: 255,
        defaultValue: null,
        aggregationType: null,
      };
      this.showError = false;
      this.modalError = '';
      this.$refs.formObserver.reset();
      this.setDefaultOptions();
    },
    hideModal() {
      this.addAnother = false;
      this.hideAttributesModal();
    },
    onReset() {

    },
  },
};
</script>

<style lang="scss">
#attributesModal {
  /deep/ .lobi-spinner {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    z-index: 1;
  }

  .expand-button {
    display: block;
    margin: 0 auto;
  }
}
</style>